import React from "react";
import PopUp from "./PopUp";
import { PopupWrapper } from "./Footer";

interface CookiePolicyModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const CookiePolicyModal: React.FC<CookiePolicyModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <PopUp
            visible={isOpen}
            onOk={onClose}
            title={"Cookie Policy"}
            onCancel={onClose}
            okText="No"
            cancelText="Yes"
        >
            <PopupWrapper>
                <div className="p-4 sm:p-6 md:p-8 rounded-lg text-left">
                    <div className="space-y-6">
                        <div>
                            <p className="text-sm text-[#6a7b76] text-left mb-2">
                                This Cookie Policy explains how Dabur India Limited ("We", "Us", "Our", "Dabur") uses cookies and similar technologies on our websites, applications, and digital platforms (collectively, "the Platform"). By using our Platform, you consent to the use of cookies in accordance with this policy.
                            </p>
                            <p className="text-sm text-[#6a7b76] text-left">
                                <strong>Last Updated:</strong> 03-11-2025
                            </p>
                        </div>

                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#4B975B] mb-2 text-left">1. What are Cookies?</h3>
                            <p className="text-sm text-[#6a7b76] text-left mb-2">
                                Cookies are small text files that are placed on your computer, smartphone, or other device when you visit a website. They are widely used to make websites work more efficiently and provide a better user experience. Cookies help the website remember your actions and preferences (such as login details, language selection, font size, and other display preferences) over a period of time, so you don't have to keep re-entering them whenever you come back to the site or browse from one page to another.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#4B975B] mb-2 text-left">2. How We Use Cookies</h3>
                            <p className="text-sm text-[#6a7b76] text-left mb-2">
                                We use cookies for various purposes, including:
                            </p>
                            <ul className="space-y-2 text-sm text-[#6a7b76] text-left list-disc pl-5">
                                <li><strong>Authentication:</strong> To identify you when you sign in to your account and keep you logged in during your session.</li>
                                <li><strong>Security:</strong> To protect your account and personal data from unauthorized access and fraudulent activities.</li>
                                <li><strong>Preferences:</strong> To remember your settings, preferences, and choices (such as language, location, and display settings).</li>
                                <li><strong>Performance and Analytics:</strong> To understand how you use our Platform, analyze traffic patterns, and improve our services.</li>
                                <li><strong>Functionality:</strong> To provide enhanced features and personalized content based on your interactions.</li>
                                <li><strong>Advertising:</strong> To deliver relevant advertisements and measure the effectiveness of our marketing campaigns.</li>
                                <li><strong>Session Management:</strong> To maintain your session as you navigate through different pages on our Platform.</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#4B975B] mb-2 text-left">3. Types of Cookies We Use</h3>

                            <div className="space-y-3 mt-3">
                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">Strictly Necessary Cookies</h4>
                                    <p className="text-sm text-[#6a7b76] text-left">
                                        These cookies are essential for the operation of our Platform. They enable core functionality such as security, network management, and accessibility. Without these cookies, certain services cannot be provided. These cookies do not gather information about you for marketing purposes.
                                    </p>
                                    <p className="text-sm text-[#6a7b76] text-left mt-1">
                                        <strong>Examples:</strong> Session cookies, authentication cookies, load balancing cookies.
                                    </p>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">Performance and Analytics Cookies</h4>
                                    <p className="text-sm text-[#6a7b76] text-left">
                                        These cookies collect information about how visitors use our Platform, such as which pages are visited most often, how much time users spend on each page, and any error messages encountered. All information collected by these cookies is aggregated and therefore anonymous. We use this information to improve how our Platform works.
                                    </p>
                                    <p className="text-sm text-[#6a7b76] text-left mt-1">
                                        <strong>Examples:</strong> Google Analytics, Umami Analytics, heatmap tools.
                                    </p>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">Functional Cookies</h4>
                                    <p className="text-sm text-[#6a7b76] text-left">
                                        These cookies allow our Platform to remember choices you make (such as your username, language, or region) and provide enhanced, more personalized features. They may also be used to provide services you have requested, such as watching a video or commenting on a blog.
                                    </p>
                                    <p className="text-sm text-[#6a7b76] text-left mt-1">
                                        <strong>Examples:</strong> Language preference cookies, user interface customization cookies.
                                    </p>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">Targeting and Advertising Cookies</h4>
                                    <p className="text-sm text-[#6a7b76] text-left">
                                        These cookies are used to deliver advertisements that are more relevant to you and your interests. They are also used to limit the number of times you see an advertisement and help measure the effectiveness of advertising campaigns. These cookies remember that you have visited our Platform and this information may be shared with other organizations, such as advertisers.
                                    </p>
                                    <p className="text-sm text-[#6a7b76] text-left mt-1">
                                        <strong>Examples:</strong> Facebook Pixel, Google Ads, retargeting cookies.
                                    </p>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">Social Media Cookies</h4>
                                    <p className="text-sm text-[#6a7b76] text-left">
                                        These cookies are set by social media services that we have added to the Platform to enable you to share our content with your friends and networks. They are capable of tracking your browser across other sites and building a profile of your interests, which may impact the content and messages you see on other websites you visit.
                                    </p>
                                    <p className="text-sm text-[#6a7b76] text-left mt-1">
                                        <strong>Examples:</strong> Facebook, Twitter, LinkedIn, Instagram sharing buttons.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#4B975B] mb-2 text-left">4. First-Party vs. Third-Party Cookies</h3>

                            <div className="space-y-2 mt-2">
                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">First-Party Cookies</h4>
                                    <p className="text-sm text-[#6a7b76] text-left">
                                        These are cookies set directly by Dabur on our Platform. We use them to remember your preferences, authenticate your session, and analyze how you use our services.
                                    </p>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">Third-Party Cookies</h4>
                                    <p className="text-sm text-[#6a7b76] text-left">
                                        These are cookies set by third-party service providers that we work with, such as analytics providers, advertising networks, and social media platforms. These cookies help us understand user behavior, deliver targeted advertising, and provide social sharing features.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#4B975B] mb-2 text-left">5. Session vs. Persistent Cookies</h3>

                            <div className="space-y-2 mt-2">
                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">Session Cookies</h4>
                                    <p className="text-sm text-[#6a7b76] text-left">
                                        These are temporary cookies that expire when you close your browser. They help our Platform remember what you've done on previous pages within the same browsing session.
                                    </p>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">Persistent Cookies</h4>
                                    <p className="text-sm text-[#6a7b76] text-left">
                                        These cookies remain on your device for a set period specified in the cookie or until you manually delete them. They help us recognize you as a returning visitor and remember your preferences across multiple sessions.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#4B975B] mb-2 text-left">6. Third-Party Services</h3>
                            <p className="text-sm text-[#6a7b76] text-left mb-2">
                                We work with the following third-party service providers who may set cookies on your device:
                            </p>
                            <ul className="space-y-1 text-sm text-[#6a7b76] text-left list-disc pl-5">
                                <li><strong>Google Analytics:</strong> For website analytics and user behavior tracking</li>
                                <li><strong>Umami Analytics:</strong> For privacy-focused website analytics</li>
                                <li><strong>Facebook Pixel:</strong> For advertising and remarketing</li>
                                <li><strong>Google Ads:</strong> For advertising campaigns and conversion tracking</li>
                                <li><strong>Social Media Platforms:</strong> For social sharing and embedded content</li>
                                <li><strong>Payment Gateways:</strong> For secure payment processing</li>
                            </ul>
                            <p className="text-sm text-[#6a7b76] text-left mt-2">
                                These third parties have their own privacy and cookie policies. We recommend reviewing their policies to understand how they use cookies and process your data.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#4B975B] mb-2 text-left">7. How to Manage Cookies</h3>
                            <p className="text-sm text-[#6a7b76] text-left mb-2">
                                You have the right to decide whether to accept or reject cookies. You can exercise your cookie preferences by adjusting your browser settings:
                            </p>

                            <div className="space-y-2 mt-2">
                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">Browser Settings</h4>
                                    <p className="text-sm text-[#6a7b76] text-left mb-1">
                                        Most web browsers allow you to control cookies through their settings. You can:
                                    </p>
                                    <ul className="space-y-1 text-sm text-[#6a7b76] text-left list-disc pl-5">
                                        <li>View and delete cookies stored on your device</li>
                                        <li>Block all cookies from being set</li>
                                        <li>Block third-party cookies only</li>
                                        <li>Clear all cookies when you close your browser</li>
                                        <li>Set exceptions for specific websites</li>
                                    </ul>
                                    <p className="text-sm text-[#6a7b76] text-left mt-2">
                                        To learn how to manage cookies in your browser, please refer to your browser's help documentation:
                                    </p>
                                    <ul className="space-y-1 text-sm text-[#6a7b76] text-left list-disc pl-5 mt-1">
                                        <li><a href="https://support.google.com/chrome/answer/95647" className="text-[#4B975B] hover:underline" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                                        <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" className="text-[#4B975B] hover:underline" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                                        <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" className="text-[#4B975B] hover:underline" target="_blank" rel="noopener noreferrer">Safari</a></li>
                                        <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" className="text-[#4B975B] hover:underline" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">Impact of Disabling Cookies</h4>
                                    <p className="text-sm text-[#6a7b76] text-left">
                                        Please note that if you choose to block or delete cookies, certain features of our Platform may not function properly. You may not be able to:
                                    </p>
                                    <ul className="space-y-1 text-sm text-[#6a7b76] text-left list-disc pl-5 mt-1">
                                        <li>Stay logged in to your account</li>
                                        <li>Save your preferences and settings</li>
                                        <li>Access personalized content</li>
                                        <li>Complete purchases or transactions</li>
                                        <li>Use certain interactive features</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold text-[#4B975B] mb-1">Opt-Out Tools</h4>
                                    <p className="text-sm text-[#6a7b76] text-left mb-1">
                                        You can also opt out of targeted advertising by visiting:
                                    </p>
                                    <ul className="space-y-1 text-sm text-[#6a7b76] text-left list-disc pl-5">
                                        <li><a href="https://optout.networkadvertising.org/" className="text-[#4B975B] hover:underline" target="_blank" rel="noopener noreferrer">Network Advertising Initiative Opt-Out</a></li>
                                        <li><a href="https://www.aboutads.info/choices/" className="text-[#4B975B] hover:underline" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance Opt-Out</a></li>
                                        <li><a href="https://www.youronlinechoices.com/" className="text-[#4B975B] hover:underline" target="_blank" rel="noopener noreferrer">Your Online Choices (Europe)</a></li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#4B975B] mb-2 text-left">8. Cookie Consent</h3>
                            <p className="text-sm text-[#6a7b76] text-left mb-2">
                                By continuing to use our Platform, you consent to our use of cookies as described in this Cookie Policy. When you first visit our Platform, you may see a cookie banner that provides you with information about cookies and allows you to accept or customize your cookie preferences.
                            </p>
                            <p className="text-sm text-[#6a7b76] text-left">
                                You can change your cookie preferences at any time by adjusting your browser settings or using the cookie management tools provided on our Platform.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#4B975B] mb-2 text-left">9. Updates to This Cookie Policy</h3>
                            <p className="text-sm text-[#6a7b76] text-left mb-2">
                                We may update this Cookie Policy from time to time to reflect changes in our practices, technologies, legal requirements, or for other operational reasons. We will notify you of any material changes by posting the updated policy on our Platform with a new "Last Updated" date.
                            </p>
                            <p className="text-sm text-[#6a7b76] text-left">
                                We encourage you to review this Cookie Policy periodically to stay informed about how we use cookies and protect your privacy.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#4B975B] mb-2 text-left">10. Contact Us</h3>
                            <p className="text-sm text-[#6a7b76] text-left mb-2">
                                If you have any questions, concerns, or requests regarding this Cookie Policy or our use of cookies, please contact us:
                            </p>
                            <div className="text-sm text-[#6a7b76] text-left">
                                <p><strong>Dabur India Limited</strong></p>
                                <p>Corporate Communication Head</p>
                                <p>Email: <a href="mailto:corpcomm@dabur.com" className="text-[#4B975B] hover:underline">corpcomm@dabur.com</a></p>
                                <p>Address: Dabur India Limited, Kaushambi Corporate Office Sahibabad, Ghaziabad - 201010, Uttar Pradesh, India</p>
                                <p>Contact Number: 0120-3962100 or 0120-4374935</p>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-gray-300">
                            <p className="text-sm text-[#6a7b76] text-left">
                                For more information about how we handle your personal data, please refer to our {" "}
                                <span
                                    onClick={() => {
                                        onClose()
                                        //   setShowPrivacyPolicy(true);
                                    }}
                                    className="text-[#4B975B] hover:underline cursor-pointer font-medium text-[10px] sm:text-xs lg:text-sm"
                                >
                                    Privacy Policy
                                </span> .
                            </p>
                        </div>
                    </div>
                </div>
            </PopupWrapper>
        </PopUp>
    );
};

export default CookiePolicyModal;
